import { FC } from 'react';
import { Box, Image, VStack } from '@chakra-ui/react';

import {
  KybDocument,
  KybDocumentType,
  KycReviewDocument,
  KycReviewDocumentType,
} from '__generated__/GQL';
import Upload, { FILE_TYPE, UploadMessage } from 'components/Upload';
import { UploadProps } from 'components/Upload/types';
import { uploadKyc } from 'features/Storage/upload';

import OBFileItem from '../../../../components/OBFileItem';
import { DocumentFile } from '../../machine/model';
import Uploading from '../Uploading';
import Clipboard from './assets/Clipboard@2x.png';
import locales from './locales';

interface BaseUploadContentProps {
  uploadFile?: UploadProps['uploadFile'];
  documentTypeToUpload?: KycReviewDocumentType | KybDocumentType;
  onSuccessfulUpload: (file: DocumentFile) => void;
  showUploaderLoading: boolean;
  uploadedDocuments:
    | Pick<KybDocument, 'kybDocumentId' | 'url' | 'type'>[]
    | Pick<KycReviewDocument, 'kycDocumentId' | 'url' | 'type'>[];
}

interface UploadContentWithoutActionsProps {
  areFileActionsDisabled: true;
}

interface UploadContentWithActionsProps {
  areFileActionsDisabled: false;
  onDeleteFileClick: (documentId: string) => void;
}

type UploadContentProps = BaseUploadContentProps &
  (UploadContentWithoutActionsProps | UploadContentWithActionsProps);

/**
 * A component that shows the uploader with the list of uploaded files, for kyc documents
 */
const UploadContent: FC<UploadContentProps> = ({
  areFileActionsDisabled = false,
  documentTypeToUpload,
  onSuccessfulUpload,
  showUploaderLoading,
  uploadedDocuments,
  uploadFile = uploadKyc,
  ...actions
}) => {
  const renderUploader = () => {
    if (showUploaderLoading) {
      return <Uploading />;
    }

    return (
      documentTypeToUpload && (
        <Upload
          errorMessage={locales.uploadFailureMessage}
          fileTypes={FILE_TYPE.ALLOWED_DOCUMENTS}
          key={`upload-host-id-${documentTypeToUpload}`}
          // A key is set to force the Upload component to be re-instantiated
          // when switching between front and back uploads
          onSuccessfulUpload={({ bucketName, fileRef, url }) => {
            onSuccessfulUpload({
              firebaseBucket: bucketName,
              firebaseRef: fileRef,
              type: documentTypeToUpload,
              url,
            });

            return Promise.resolve();
          }}
          uploadFile={uploadFile}
        >
          <UploadMessage
            dragAndDropMessage={
              locales.getString(`uploadTitle.${documentTypeToUpload}`) ||
              locales.uploadTitle.default
            }
            imageElement={<Image boxSize="120px" src={Clipboard} />}
          />
        </Upload>
      )
    );
  };

  return (
    <Box>
      {renderUploader()}

      {uploadedDocuments.length > 0 ? (
        <VStack alignItems="stretch" marginTop="space-12">
          {uploadedDocuments.map((document) => {
            const { type, url } = document;

            if (!url) {
              throw new Error('url shouldn’t be undefined');
            }

            const documentId =
              (document as KycReviewDocument).kycDocumentId ||
              (document as KybDocument).kybDocumentId;

            const onDeleteClick = () => {
              if (!areFileActionsDisabled) {
                (actions as UploadContentWithActionsProps).onDeleteFileClick(
                  documentId,
                );
              }
            };

            return (
              <OBFileItem
                areButtonsDisabled={areFileActionsDisabled}
                fileName={locales.getString(type) || locales.file}
                key={type}
                onDeleteClick={onDeleteClick}
                onPreviewClick={() => window.open(url, '_blank')}
              />
            );
          })}
        </VStack>
      ) : null}
    </Box>
  );
};

export default UploadContent;
